import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, Product } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import './Home.css';
import { proxyBucketUrl, getProductImageUrl, handleImageError } from '../utils/imageUtils';
import HotspotTooltipLayer from '../components/HotspotTooltipLayer';
import { useFavorites } from '../contexts/FavoritesContext';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isFavorite: isFavoriteProduct, toggleFavorite: toggleFavoriteProduct } = useFavorites();
  const [hoveredSpot, setHoveredSpot] = useState<number | null>(null);

  // Add-to-cart / favourite buttons inside a hotspot tooltip
  const addHotspotToCart = async (productId: number) => {
    if (!api.isAuthenticated()) {
      showToast('Please login to add items to cart', 'error');
      return;
    }
    try {
      await api.addToCart({ productId, quantity: 1 });
      showToast('Added to cart!', 'success');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showToast('Failed to add to cart. Please try again.', 'error');
    }
  };

  const toggleHotspotFavorite = async (productId: number) => {
    try {
      await toggleFavoriteProduct(productId);
    } catch {
      showToast('Failed to update favorites. Please try again.', 'error');
    }
  };

  const toggleHotspot = (id: number) => {
    setHoveredSpot(prev => (prev === id ? null : id));
  };

  // Desktop (real mouse) opens tooltips on hover; touch keeps tap-to-open.
  // Closing waits a moment so the pointer can cross the gap into the tooltip.
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const hoverCloseTimer = useRef<number | undefined>(undefined);
  const openHotspotOnHover = (id: number) => {
    if (!canHover) return;
    window.clearTimeout(hoverCloseTimer.current);
    setHoveredSpot(id);
  };
  const closeHotspotOnHover = () => {
    if (!canHover) return;
    hoverCloseTimer.current = window.setTimeout(() => setHoveredSpot(null), 200);
  };

  // Close the open tooltip on any click outside it, instead of on hover-out.
  useEffect(() => {
    if (hoveredSpot === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.hotspot, .hotspot-tooltip')) {
        setHoveredSpot(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [hoveredSpot]);

  const [hotTrendProducts, setHotTrendProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState<{ [categoryName: string]: { count: number; minPrice: number; maxPrice: number } }>({});
  const [allLooks, setAllLooks] = useState<any[]>([]);
  const [currentLookIndex, setCurrentLookIndex] = useState(0);
  const [favorites, setFavorites] = useState<number[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const rawLook = allLooks[currentLookIndex] || null;
  const shopLookData = rawLook ? { ...rawLook, hotspots: rawLook.hotspots || [] } : null;

  // Swipe/drag between different looks - works with touch AND mouse via
  // Pointer Events (the arrows still switch between a single look's own
  // images, unchanged). The image visually follows the pointer while
  // dragging, then either flings on to the next/prev look or snaps back.
  const dragStartX = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [suppressDragTransition, setSuppressDragTransition] = useState(false);

  const goToNextLook = () => {
    if (allLooks.length < 2) return;
    setCurrentLookIndex(prev => (prev + 1) % allLooks.length);
    setCurrentSlide(0);
  };

  const goToPrevLook = () => {
    if (allLooks.length < 2) return;
    setCurrentLookIndex(prev => (prev - 1 + allLooks.length) % allLooks.length);
    setCurrentSlide(0);
  };

  const handleDragStart = (e: React.PointerEvent) => {
    if (allLooks.length < 2) return;
    dragStartX.current = e.clientX;
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handleDragMove = (e: React.PointerEvent) => {
    if (dragStartX.current === null) return;
    setDragOffset(e.clientX - dragStartX.current);
  };

  const handleDragEnd = () => {
    if (dragStartX.current === null) return;
    const delta = dragOffset;
    dragStartX.current = null;
    setIsDragging(false);

    const threshold = 60;
    if (Math.abs(delta) > threshold) {
      // Fling the current image the rest of the way off-screen, then swap
      // to the next/prev look and reset instantly (no transition) so the
      // new image appears fresh in place.
      setDragOffset(delta < 0 ? -window.innerWidth : window.innerWidth);
      window.setTimeout(() => {
        setSuppressDragTransition(true);
        if (delta < 0) {
          goToNextLook();
        } else {
          goToPrevLook();
        }
        setDragOffset(0);
        requestAnimationFrame(() => setSuppressDragTransition(false));
      }, 220);
    } else {
      setDragOffset(0);
    }
  };

  const handleCategoryClick = (categoryName: string) => {
    // Navigate to shop page with category filter
    navigate(`/shop?category=${categoryName.toLowerCase()}`);
  };

  const handleProductClick = (productId: number) => {
    // Navigate to product detail page
    navigate(`/product/${productId}`);
  };

  const loadUserFavorites = async () => {
    if (api.isAuthenticated()) {
      try {
        const userFavorites = await api.getFavorites();
        setFavorites(userFavorites.map(product => product.id));
      } catch (error) {
        console.error('Failed to load user favorites:', error);
      }
    }
  };

  const toggleFavorite = async (productId: number) => {
    if (!api.isAuthenticated()) {
      showToast('Please login to add favorites', 'error');
      return;
    }
    
    try {
      const isFavorite = favorites.includes(productId);
      
      if (isFavorite) {
        await api.removeFromFavorites(productId);
        setFavorites(prev => prev.filter(id => id !== productId));
      } else {
        await api.addToFavorites(productId);
        setFavorites(prev => [...prev, productId]);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      showToast('Failed to update favorites. Please try again.', 'error');
    }
  };

  const nextSlide = () => {
    if (shopLookData && shopLookData.images) {
      setCurrentSlide(prev => prev < shopLookData.images.length - 1 ? prev + 1 : 0);
    }
  };

  const prevSlide = () => {
    if (shopLookData && shopLookData.images) {
      setCurrentSlide(prev => prev > 0 ? prev - 1 : shopLookData.images.length - 1);
    }
  };


  useEffect(() => {
    const loadData = async () => {
      try {
        // Load hot trend products, category statistics, and shop the look data in parallel
        const [products, stats, lookData] = await Promise.all([
          api.getHotTrendProducts(),
          api.getCategoryStatistics(),
          api.getShopTheLookData()
        ]);
        
        setHotTrendProducts(products);
        setCategoryStats(stats);
        
        // Load shop the look data (product details already included)
        if (lookData) {
          console.log('✅ Shop the Look loaded:', lookData);
          // Handle both single outfit object and array of outfits
          if (Array.isArray(lookData) && lookData.length > 0) {
            setAllLooks(lookData);
          } else if (!Array.isArray(lookData)) {
            // Single outfit object
            setAllLooks([lookData]);
          } else {
            setAllLooks([]);
          }
        } else {
          setAllLooks([]);
        }
        setCurrentLookIndex(0);
        
        // Load user favorites
        await loadUserFavorites();
      } catch (error) {
        console.error('Failed to load homepage data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);


  return (
    <div className="home">
      <section className="hero">
        <div className="hero-container">
          <div className="hero-cards-column">
            <div className="hero-card">
              <img src="/hero-icons/zari.png" alt="Dice" className="hero-card-icon" />
              <h3>'90s vibes ✮</h3>
            </div>
            <div className="hero-card">
              <img src="/hero-icons/heart.png" alt="Heart" className="hero-card-icon" />
              <h3>'Y2K' lover ♡</h3>
            </div>
            <div className="hero-card">
              <img src="/hero-icons/roz-mpala.png" alt="Moon" className="hero-card-icon" />
              <h3>'70s grooves ✿</h3>
            </div>
            <div className="hero-card">
              <img src="/hero-icons/cd.png" alt="CD" className="hero-card-icon" />
              <h3>Sunglasses .+.</h3>
            </div>
            <div className="hero-card">
              <img src="/hero-icons/disco.png" alt="Disco" className="hero-card-icon" />
              <h3>Style bundles ⚧</h3>
            </div>
          </div>
          
          <video 
            className="hero-video" 
            autoPlay 
            muted 
            loop 
            playsInline 
            controls={false}
            disablePictureInPicture
          >
            <source src="/videos/hero.mov" type="video/mp4" />
            Το browser σας δεν υποστηρίζει video.
          </video>
        </div>
      </section>

      <section className="home-features">
        <div className="home-features-container">
          <div className="home-features-grid">
            <div className="home-feature-item">
              <img src="/hero-icons/gems.png" alt="Gems" className="home-feature-icon" />
              <h4>Curated Vintage Gems</h4>
            </div>
            <div className="home-feature-item">
              <img src="/hero-icons/shipping.png" alt="Shipping" className="home-feature-icon" />
              <h4>Fast & Reliable Shipping</h4>
            </div>
            <div className="home-feature-item">
              <img src="/hero-icons/recicle.png" alt="Hand-picked" className="home-feature-icon" />
              <h4>Ethical Shopping</h4>
            </div>
            <div className="home-feature-item">
              <img src="/hero-icons/exchange.png" alt="Exchange" className="home-feature-icon" />
              <h4>10-day Exchange Window</h4>
            </div>
          </div>
        </div>
      </section>

      <section className="shop-the-look-title">
        <div className="container">
          <div
            className={`shop-the-look-title-track ${allLooks.length > 1 ? 'draggable' : ''}`}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
            style={{ touchAction: 'pan-y' }}
          >
            <h2>✮ SHOP THE LOOK ✮</h2>
          </div>
        </div>
      </section>

      <section className="interactive-outfit">
        <div className="container">
          {shopLookData ? (
            <div
              className={`outfit-container ${allLooks.length > 1 ? 'draggable' : ''}`}
              onPointerDown={handleDragStart}
              onPointerMove={handleDragMove}
              onPointerUp={handleDragEnd}
              onPointerCancel={handleDragEnd}
              style={{
                transform: `translateX(${dragOffset}px)`,
                transition: (isDragging || suppressDragTransition) ? 'none' : 'transform 0.25s ease',
                touchAction: 'pan-y',
              }}
            >
              <div className="outfit-image-wrapper">
                <div className="outfit-image">
                  {(() => {
                    const images = shopLookData.images || [shopLookData.image];
                    const currentImage = images[currentSlide];
                    
                    return (
                      <>
                        <div 
                          className="outfit-image-slide"
                          style={{backgroundImage: `url(${proxyBucketUrl(currentImage)})`}}
                        />
                        
                        {/* Show slide navigation arrows if multiple images */}
                        {(() => {
                          const images = shopLookData.images || [shopLookData.image];
                          if (images.length > 1) {
                            return (
                              <>
                                <button
                                  className="slide-nav-arrow slide-nav-left"
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={prevSlide}
                                >
                                  &lt;
                                </button>
                                <button
                                  className="slide-nav-arrow slide-nav-right"
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={nextSlide}
                                >
                                  &gt;
                                </button>
                              </>
                            );
                          }
                          return null;
                        })()}
                      </>
                    );
                  })()}
                  
                  {/* Show hotspots only on first slide */}
                  {currentSlide === 0 && shopLookData.hotspots && Array.isArray(shopLookData.hotspots) && shopLookData.hotspots.map((hotspot: any, index: number) => (
                    <div
                      key={hotspot.id}
                      className={`hotspot clickable ${hoveredSpot === hotspot.id ? 'hotspot--open' : ''}`}
                      style={{ top: `${hotspot.y}%`, left: `${hotspot.x}%` }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseEnter={() => openHotspotOnHover(hotspot.id)}
                      onMouseLeave={closeHotspotOnHover}
                      onClick={() => {
                        if (!canHover) {
                          toggleHotspot(hotspot.id);
                        } else if (hotspot.product && hotspot.productId !== -1) {
                          // Desktop: hover shows the tooltip, a click goes straight to the product
                          handleProductClick(hotspot.productId);
                        }
                      }}
                    >
                      <div className="hotspot-dot">
                        <div className="hotspot-pulse"></div>
                        <div className="hotspot-ring"></div>
                      </div>
                      {hoveredSpot === hotspot.id && (
                        <HotspotTooltipLayer>
                          <div
                            className={`hotspot-tooltip hotspot-tooltip--glass ${hotspot.product && hotspot.productId !== -1 ? 'hotspot-tooltip--clickable' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hotspot.product && hotspot.productId !== -1) handleProductClick(hotspot.productId);
                            }}
                          >
                            {hotspot.productId === -1 ? (
                              <div style={{ textAlign: 'center', padding: '0.6rem 0.8rem' }}>
                                <h4 style={{ color: '#1a1a1a', margin: '0 0 0.2rem 0', fontSize: '0.95rem' }}>{hotspot.customTitle || 'SOLD'}</h4>
                                <div style={{ fontSize: '1.1rem', margin: '0.1rem 0', transform: 'rotate(90deg)', display: 'inline-block', color: '#1a1a1a' }}>:(</div>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#4b4b4b', lineHeight: '1.2' }}>Unfortunately, this product is no longer available.</p>
                              </div>
                            ) : hotspot.product ? (
                              <>
                                <div className="tooltip-product">
                                  <img className="tooltip-thumb" src={getProductImageUrl(hotspot.product)} alt="" onError={handleImageError} />
                                  <div className="tooltip-body">
                                    <h4 className="tooltip-title">{hotspot.product.name}</h4>
                                    <div className="tooltip-footer">
                                      <span className="hotspot-price">€{hotspot.product.price.toFixed(2)}</span>
                                      <div className="tooltip-actions">
                                        <button
                                          className={`hotspot-icon-btn ${isFavoriteProduct(hotspot.productId) ? 'active' : ''}`}
                                          aria-label="Add to favorites" onPointerDown={(e) => e.stopPropagation()}
                                          onClick={(e) => { e.stopPropagation(); toggleHotspotFavorite(hotspot.productId); }}
                                        >
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavoriteProduct(hotspot.productId) ? 'currentColor' : 'none'} xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                                          </svg>
                                        </button>
                                        <button
                                          className="hotspot-icon-btn"
                                          aria-label="Add to cart" onPointerDown={(e) => e.stopPropagation()}
                                          onClick={(e) => { e.stopPropagation(); addHotspotToCart(hotspot.productId); }}
                                        >
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6.2 9h11.6l0.9 11a1 1 0 0 1-1 1.1H6.3a1 1 0 0 1-1-1.1L6.2 9z" />
                                            <path d="M9.6 9V5.9a2.4 2.4 0 0 1 4.8 0V9" />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : null}
                            <div className="tooltip-arrow"></div>
                          </div>
                        </HotspotTooltipLayer>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="outfit-info">
                <div className="info-header">
                  <h2>{shopLookData.title}</h2>
                  <div className="header-decoration">
                    <div className="deco-line"></div>
                    <div className="deco-dot"></div>
                    <div className="deco-line"></div>
                  </div>
                </div>

                <p className="look-description">{shopLookData.description}</p>

                <div className="view-all-looks-container view-all-looks-container--primary">
                  <button
                    className="view-all-looks-btn"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => navigate('/outfits')}
                  >
                    View All Looks
                    <span className="btn-arrow">→</span>
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <section className="favorite-categories">
        <div className="container">
          <div className="favorite-categories-title-track">
            <h2>✮ FAVORITE CATEGORIES ✮</h2>
          </div>
          <div className="categories-grid">
            {/* Dresses Category */}
            <div className="catalog-card" onClick={() => handleCategoryClick('Dresses')}>
              <div 
                className="catalog-image"
                style={{backgroundImage: 'url(/images/dresses.jpeg)'}}
              >
                <div className="catalog-overlay">
                  <h3 className="catalog-title">Dresses</h3>
                </div>
              </div>
            </div>

            {/* Tops Category */}
            <div className="catalog-card" onClick={() => handleCategoryClick('Tops')}>
              <div 
                className="catalog-image"
                style={{backgroundImage: 'url(/images/top.jpeg)'}}
              >
                <div className="catalog-overlay">
                  <h3 className="catalog-title">Tops</h3>
                </div>
              </div>
            </div>

            {/* Bottoms Category */}
            <div className="catalog-card" onClick={() => handleCategoryClick('Bottoms')}>
              <div 
                className="catalog-image"
                style={{backgroundImage: 'url(/images/shorts.jpeg)'}}
              >
                <div className="catalog-overlay">
                  <h3 className="catalog-title">Bottoms</h3>
                </div>
              </div>
            </div>

            {/* Accessories Category */}
            <div className="catalog-card" onClick={() => handleCategoryClick('Accessories')}>
              <div 
                className="catalog-image"
                style={{backgroundImage: 'url(/images/sunglasses.jpeg)'}}
              >
                <div className="catalog-overlay">
                  <h3 className="catalog-title">Accessories</h3>
                </div>
              </div>
            </div>
          </div>
          
          {/* View All Categories Button */}
          <div className="view-all-categories-container">
            <button 
              className="view-all-categories-btn"
              onClick={() => navigate('/catalog')}
            >
              View All Categories
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>


      <section className="store-visit">
        <div className="container">
          <div className="visit-content">
            <div className="visit-left">
              <h2>Visit Our Store</h2>
              <div className="visit-info">
                <div className="info-item">
                  <span className="info-label">Location:</span>
                  <span className="info-value">Vasileos Irakliou, Thessaloniki</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Hours:</span>
                  <span className="info-value">Monday-Friday 10:00-20:00</span>
                </div>
              </div>

              {/* Mobile-only: shows right above the button; the desktop copy
                  below stays side-by-side with this column instead. */}
              <div
                className="visit-image visit-image--mobile"
                style={{backgroundImage: 'url(https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=500&fit=crop&auto=format)'}}
              ></div>

              <button
                className="visit-button"
                onClick={() => window.open('https://www.google.com/maps/place/ECO+FASHION+%E2%9C%AE/@40.6343726,22.9391734,17z/data=!3m1!4b1!4m6!3m5!1s0x14a8398e0476f50d:0x317bd24f5a059072!8m2!3d40.6343686!4d22.9417483!16s%2Fg%2F11zk20cdk3?entry=ttu&g_ep=EgoyMDI2MDYxNi4wIKXMDSoASAFQAw%3D%3D', '_blank')}
              >
                See Location
              </button>
            </div>
            <div
              className="visit-image visit-image--desktop"
              style={{backgroundImage: 'url(https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=500&fit=crop&auto=format)'}}
            ></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;